<template>
  <div>
    <Navbar />
    <div class="search-page">
      <el-card class="search-card" shadow="always">
        <template #header>
          <h3>Rechercher</h3>
        </template>
        <el-tabs v-model="activeTab">
          <el-tab-pane label="Membres" name="users">
            <p class="description">
              Recherchez un autre membre de Wedoomotion par son nom d'utilisateur.
            </p>
            <el-form label-position="top" @submit.prevent="searchUser">
              <el-form-item label="Nom d'utilisateur">
                <el-input
                  v-model="promptedUsername"
                  placeholder="Pseudo (ou une partie)"
                  :prefix-icon="Search"
                  clearable
                  @keyup.enter="searchUser"
                />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="searchUser">
                  Rechercher
                </el-button>
              </el-form-item>
            </el-form>
            <el-alert
              v-if="errorMessage"
              :title="errorMessage"
              type="error"
              show-icon
              :closable="false"
            />
            <ul v-if="foundUsers.length" class="results">
              <li v-for="user in foundUsers" :key="user.id">
                <UserLink :username="user.username" />
              </li>
            </ul>
          </el-tab-pane>

          <el-tab-pane label="Publications" name="posts">
            <p class="description">
              Recherchez un mot ou une expression dans les articles et les chippies.
            </p>
            <el-form label-position="top" @submit.prevent="searchPosts">
              <el-form-item label="Mot-clé">
                <el-input
                  v-model="promptedKeyword"
                  placeholder="Mot-clé"
                  :prefix-icon="Search"
                  clearable
                  class="keyword-input"
                  @keyup.enter="searchPosts"
                />
              </el-form-item>
              <el-form-item label="Rubrique">
                <el-radio-group v-model="postType">
                  <el-radio-button value="">Toutes</el-radio-button>
                  <el-radio-button value="ARTICLE">Centres d'intérêt</el-radio-button>
                  <el-radio-button value="CHIPPIES">Club des Chippies</el-radio-button>
                </el-radio-group>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" class="search-posts-btn" @click="searchPosts">
                  Rechercher
                </el-button>
              </el-form-item>
            </el-form>
            <el-alert
              v-if="postErrorMessage"
              :title="postErrorMessage"
              type="error"
              show-icon
              :closable="false"
            />
            <ul v-if="foundPosts.length" class="results post-results">
              <li v-for="post in foundPosts" :key="post.id">
                <router-link :to="{ name: 'Post', params: { postId: post.id } }" class="post-result-link">
                  <el-tag size="small" :type="post.type === 'ARTICLE' ? 'warning' : 'info'">
                    {{ post.type === 'ARTICLE' ? 'Article' : 'Chippie' }}
                  </el-tag>
                  {{ excerpt(post.content) }}
                </router-link>
                <div class="post-result-meta">
                  <UserLink :username="post.User && post.User.username" />
                  · {{ new Date(post.createdAt).toLocaleDateString('fr-FR') }}
                </div>
              </li>
            </ul>
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue';
import UserLink from '@/components/UserLink.vue';
import api from '@/api';
import { Search } from '@element-plus/icons-vue';

export default {
  components: {
    Navbar,
    UserLink,
  },

  data() {
    return {
      activeTab: 'users',
      promptedUsername: '',
      foundUsers: [],
      promptedKeyword: '',
      postType: '',
      foundPosts: [],
      postErrorMessage: '',
      errorMessage: '',
    };
  },

  computed: {
    Search() {
      return Search;
    },
  },

  mounted() {
    document.body.style.backgroundColor = 'rgba(21, 109, 123)';
  },

  methods: {
    // Recherche par pseudo (GET /search/users) : accessible à tous les membres,
    // contrairement à /user/by-email réservé aux ADMIN
    async searchUser() {
      const username = this.promptedUsername.trim();
      this.foundUsers = [];
      if (!username) {
        this.errorMessage = 'Saisissez un nom d\'utilisateur.';
        return;
      }

      try {
        const response = await api.get(
          `/search/users?username=${encodeURIComponent(username)}`
        );
        const users = (response.data && response.data.users) || [];

        if (users.length === 0) {
          this.errorMessage = 'Aucun utilisateur trouvé.';
        } else if (users.length === 1) {
          // Un seul résultat : on ouvre directement son profil
          this.errorMessage = '';
          this.$router.push({ path: `/profil/${encodeURIComponent(users[0].username)}` });
        } else {
          this.errorMessage = '';
          this.foundUsers = users;
        }
      } catch (error) {
        this.errorMessage = 'Une erreur est survenue lors de la recherche.';
      }
    },

    // Recherche de publications par mot-clé (GET /search/posts), filtrable par rubrique
    async searchPosts() {
      const keyword = this.promptedKeyword.trim();
      this.foundPosts = [];
      if (!keyword) {
        this.postErrorMessage = 'Saisissez un mot-clé.';
        return;
      }

      const params = new URLSearchParams({ keyword });
      if (this.postType) params.append('type', this.postType);

      try {
        const response = await api.get(`/search/posts?${params.toString()}`);
        const posts = (response.data && response.data.posts) || [];
        this.postErrorMessage = posts.length ? '' : 'Aucune publication trouvée.';
        this.foundPosts = posts;
      } catch (error) {
        this.postErrorMessage = 'Une erreur est survenue lors de la recherche.';
      }
    },

    excerpt(text) {
      if (!text) return '';
      return text.length > 140 ? `${text.slice(0, 140)}…` : text;
    },
  },
};
</script>

<style scoped>
.search-page {
  padding: 70px 20px 20px;
  display: flex;
  justify-content: center;
}

.search-card {
  width: 100%;
  max-width: 600px;
}

.search-card h3 {
  margin: 0;
  color: #303133;
}

.description {
  color: #606266;
  margin-bottom: 20px;
}

.results {
  list-style: none;
  padding: 0;
  margin: 16px 0 0;
}

.results li {
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
}

.post-result-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #303133;
  text-decoration: none;
}

.post-result-link:hover {
  text-decoration: underline;
}

.post-result-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
</style>
